import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { stopPortalEvents } from './portalGuard';

const QUICK_NOTES = [
  { id: 'resolved', label: 'Resolved', note: 'Resolved.' },
  { id: 'no_further_action', label: 'No further action', note: 'No further action needed.' },
] as const;

export interface ArchiveSubject {
  patientName: string;
  /** Already formatted for reading, e.g. "12 Aug". */
  serviceDate: string;
  payerName: string;
}

export interface ArchiveDialogProps {
  open: boolean;
  subject: ArchiveSubject;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (opts: { note?: string }) => void;
}

/**
 * Archive, with the sentence that makes it safe to press (GDN-3380).
 *
 * A dialog rather than a menu item that just fires, because archive is the
 * one verb here with no visible end date: the biller is told, before they
 * commit, both how wide it reaches (every claim for the encounter, not the
 * claim they happen to be looking at) and that it is not permanent. The hide
 * rule already guarantees the second half; saying so is what stops billers
 * from avoiding the verb and letting the queue grow instead.
 *
 * The note is optional and is the only place the reason gets recorded. Its
 * helper says who can see it, because a biller writing "patient is a friend
 * of the owner, do not chase" deserves to know their colleagues will read it.
 */
export function ArchiveDialog({ open, subject, busy, onCancel, onConfirm }: ArchiveDialogProps) {
  const { billing } = useEventTracking();
  const [note, setNote] = useState('');
  const noteId = useId();
  const noteInput = useRef<HTMLTextAreaElement>(null);

  /* A reopened dialog is a new decision, not a resumed one. */
  useEffect(() => {
    if (open) setNote('');
  }, [open]);

  const confirm = useCallback(() => {
    onConfirm({ note: note.trim() || undefined });
  }, [note, onConfirm]);

  return (
    <Dialog {...stopPortalEvents} fullWidth maxWidth="xs" open={open} onClose={onCancel}>
      <DialogTitle sx={{ fontSize: '1.0625rem', fontWeight: 600, pb: 1 }}>
        Archive claims
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 600, mb: 1.5 }}>
          {subject.patientName}
          <Typography
            component="span"
            sx={{
              display: 'block',
              color: 'text.secondary',
              fontSize: 'inherit',
              lineHeight: 'inherit',
            }}
          >
            {subject.serviceDate} · {subject.payerName}
          </Typography>
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', mb: 1 }}>
          Hides all claims for this encounter from all queues.
        </Typography>
        <Typography
          sx={{ color: 'text.secondary', fontSize: '0.8125rem', lineHeight: '1.1875rem', mb: 2 }}
        >
          Claims return automatically if the payer responds or a corrected claim is created.
        </Typography>
        <Typography
          component="label"
          htmlFor={noteId}
          sx={{ display: 'block', fontSize: '0.875rem', lineHeight: '1.25rem', mb: 1 }}
        >
          Note (optional)
        </Typography>
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          {QUICK_NOTES.map(quickNote => (
            <Button
              key={quickNote.label}
              disabled={busy}
              onClick={() => {
                billing.archiveQuickNoteClicked({ preset: quickNote.id });
                setNote(current =>
                  current.split('\n').some(line => line.trim() === quickNote.note)
                    ? current
                    : [current.trimEnd(), quickNote.note].filter(Boolean).join('\n'),
                );
                noteInput.current?.focus();
              }}
              size="small"
              sx={{ borderRadius: 4, textTransform: 'none' }}
              variant="outlined"
            >
              {quickNote.label}
            </Button>
          ))}
        </Stack>
        <TextField
          autoFocus
          disabled={busy}
          fullWidth
          id={noteId}
          inputRef={noteInput}
          multiline
          minRows={2}
          placeholder="Add a note…"
          value={note}
          onChange={event => setNote(event.target.value)}
          /* Enter commits, Shift+Enter breaks the line. The note is one or two
             sentences in practice, and a biller who has typed the reason wants
             the same key that ends every other short form in the app. */
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (!busy) confirm();
            }
          }}
          helperText="Visible to everyone at the clinic, with your name and time."
          slotProps={{
            input: { sx: { fontSize: '0.875rem', lineHeight: '1.25rem' } },
            formHelperText: { sx: { mx: 0, lineHeight: '1.125rem' } },
          }}
        />
      </DialogContent>
      {/* Sentence case, not MUI's default SHOUTING: the mockup reads "Cancel"
          and "Archive", and an uppercased label is a different word on the
          screen from the one the copy deck pins. */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onCancel} size="small" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          disabled={busy}
          onClick={confirm}
          size="small"
          sx={{ textTransform: 'none' }}
          variant="contained"
        >
          Archive
        </Button>
      </DialogActions>
    </Dialog>
  );
}
